import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Footer } from "../components/Footer";
import {
  ArrowLeft, Download, Network, TrainFront, Building2, Sprout,
  FileText, Landmark, Shield, Layers, ArrowRight, AlertTriangle,
} from "lucide-react";
import { useI18n } from "../i18n";
import { LanguageSwitcher } from "../components/LanguageSwitcher";

const PDF_URL =
  "https://customer-assets-jai6qajn.emergentagent.net/job_candidat/artifacts/ly66dn3b_Programme%202027%20GC.pdf";

const KEYVALS = ["133–151 Md€", "5,45 Md€/an", "8"];
const CHANTIERS = [
  { icon: Network, n: "01", tk: "prog.c1t", lead: "pg.c1.lead", pts: "pg.c1.points" },
  { icon: TrainFront, n: "02", tk: "prog.c2t", lead: "pg.c2.lead", pts: "pg.c2.points" },
  { icon: Building2, n: "03", tk: "prog.c3t", lead: "pg.c3.lead", pts: "pg.c3.points" },
  { icon: Sprout, n: "04", tk: "prog.c4t", lead: "pg.c4.lead", pts: "pg.c4.points" },
  { icon: FileText, n: "05", tk: "prog.c5t", lead: "pg.c5.lead", pts: "pg.c5.points" },
  { icon: Landmark, n: "06", tk: "prog.c6t", lead: "pg.c6.lead", pts: "pg.c6.points" },
  { icon: Shield, n: "07", tk: "prog.c7t", lead: "pg.c7.lead", pts: "pg.c7.points" },
  { icon: Layers, n: "08", tk: "prog.c8t", lead: "pg.c8.lead", pts: "pg.c8.points" },
];
const FIN_VALUES = ["133–151 Md€", "+3,6 à 7,2 Md€", "+5,45 Md€/an", "≈ 10%"];
const TIMELINE_YEARS = ["2027", "2028", "2029", "2030"];

const ProgrammePage = () => {
  const { t } = useI18n();
  useEffect(() => { window.scrollTo(0, 0); }, []);
  const kf = t("pg.kf");
  const fin = t("pg.fin");
  const timeline = t("pg.timeline");
  const vig = t("pg.vig");

  return (
    <div className="bg-[#F8F9FA]">
      <div className="bg-[#0B1121] text-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="programme-back" className="flex items-center gap-2 text-white/70 hover:text-white text-sm transition-colors">
            <ArrowLeft size={16} /> {t("pg.back")}
          </Link>
          <div className="flex items-center gap-3">
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer" data-testid="programme-pdf"
              className="flex items-center gap-2 text-sm bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg transition-colors">
              <Download size={16} /> {t("pg.pdf")}
            </a>
            <LanguageSwitcher dark />
          </div>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pt-16 pb-12 sm:pt-24 sm:pb-16">
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E1000F] mb-5">{t("pg.eyebrow")}</p>
        <h1 className="font-display font-black tracking-tighter text-5xl sm:text-6xl lg:text-7xl text-[#0B1121] leading-[0.92] max-w-4xl">
          {t("pg.title")}
        </h1>
        <p className="mt-6 max-w-2xl text-lg text-slate-600 leading-relaxed">{t("pg.intro")}</p>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-slate-200 rounded-2xl overflow-hidden border border-slate-200">
          {KEYVALS.map((v, i) => (
            <div key={i} className="bg-white p-6">
              <div className="font-display font-black text-3xl text-[#001489]">{v}</div>
              <p className="mt-1 text-sm text-slate-600">{Array.isArray(kf) ? kf[i] : ""}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-[#0B1121] mb-10">{t("pg.chantiersTitle")}</h2>
        <div className="space-y-6">
          {CHANTIERS.map((c) => {
            const Icon = c.icon;
            const points = t(c.pts);
            return (
              <div key={c.n} data-testid={`programme-detail-${c.n}`}
                className="bg-white border border-slate-200 rounded-2xl p-7 sm:p-9 grid lg:grid-cols-12 gap-6">
                <div className="lg:col-span-4">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-[#001489] flex items-center justify-center shrink-0">
                      <Icon className="text-white" size={24} />
                    </div>
                    <span className="font-mono-accent text-2xl text-slate-300">{c.n}</span>
                  </div>
                  <h3 className="mt-5 font-display font-extrabold tracking-tight text-2xl text-[#0B1121]">{t(c.tk)}</h3>
                  <p className="mt-2 text-slate-600">{t(c.lead)}</p>
                </div>
                <div className="lg:col-span-8 lg:border-l lg:border-slate-100 lg:pl-8">
                  <ul className="space-y-3">
                    {(Array.isArray(points) ? points : []).map((p, i) => (
                      <li key={i} className="flex gap-3 text-slate-700 leading-relaxed">
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#E1000F] shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#0B1121] py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-[0.12]" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 relative">
          <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#38BDF8] mb-4">{t("pg.finTitle")}</p>
          <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-white mb-10">{t("pg.finHead")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {FIN_VALUES.map((v, i) => (
              <div key={i} className="border border-white/10 rounded-2xl p-6">
                <div className="font-display font-black text-2xl text-white">{v}</div>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{Array.isArray(fin) ? fin[i] : ""}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#E1000F] mb-4">{t("pg.implEyebrow")}</p>
        <h2 className="font-display font-black tracking-tighter text-3xl sm:text-4xl text-[#0B1121] mb-10">{t("pg.implTitle")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {TIMELINE_YEARS.map((year, i) => {
            const item = Array.isArray(timeline) ? timeline[i] : {};
            return (
              <div key={year} className="bg-white border border-slate-200 rounded-2xl p-6">
                <div className="font-display font-black text-4xl text-[#001489]">{year}</div>
                <span className="mt-2 inline-block font-mono-accent text-[10px] uppercase tracking-widest text-[#E1000F]">{item?.tag}</span>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item?.text}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 sm:px-8 pb-16 sm:pb-24">
        <div className="bg-white border border-slate-200 rounded-3xl p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-6">
            <AlertTriangle className="text-[#E1000F]" size={22} />
            <h2 className="font-display font-extrabold tracking-tight text-2xl text-[#0B1121]">{t("pg.vigTitle")}</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4">
            {(Array.isArray(vig) ? vig : []).map((v, i) => (
              <li key={i} className="flex gap-3 text-slate-700 leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-slate-400 shrink-0" />
                <span>{v}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12 text-center">
          <Link to="/#adhesion" data-testid="programme-cta-adhesion"
            className="group inline-flex items-center gap-2 bg-[#0B1121] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#001489] transition-all hover:scale-[1.02]">
            {t("pg.ctaAdh")}
            <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ProgrammePage;
